import { Fragment, useEffect, useRef, useState } from 'react';

import {
  IoChevronDown,
  IoChevronUp,
  IoLogOut,
  IoReturnUpBack,
} from 'react-icons/io5';
import { MdDeleteForever, MdOutlineDirectionsWalk } from 'react-icons/md';
import { FaRegCheckCircle } from 'react-icons/fa';
import { FaCircleArrowLeft, FaCircleArrowRight } from 'react-icons/fa6';

import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { ClockLoader, ScaleLoader } from 'react-spinners';

import styles from './styles/styles.module.css';

import {
  clearState,
  setFirst,
  setLast,
  setPlate,
  setType,
  setVendorCompany,
} from '../../Redux/slices/visitorSlice';

import {
  addVisitor,
  getSiteVisitorsByDay,
  logoutVisitor,
  returnVisitor,
  searchVisitors,
  temporaryLeaveVisitor,
} from '../../api/visitors/post';

import {
  invalidateEntry,
  updateLogEntriesAndVisitors,
  updateLogEntryTimes,
} from '../../api/logEntries/post';

import VisitorProfile from '../../components/VisitorProfile/VisitorProfile';
import { setLogEntires } from '../../Redux/slices/logEntires';
import resolveOperationalMomentFromHHmm from '../../utils/resolveOperationalMomentFromHHmm';
import { getSiteDateKey } from './utils/getSiteDateKey';

import {
  setVisitorLog,
  selectVisitorLogRealtimeVersion,
} from '../../Redux/slices/visitorLogSlice';

type VisitorMovementLogType =
  'VISITOR_IN' | 'VISITOR_TEMP_OUT' | 'VISITOR_TEMP_IN' | 'VISITOR_OUT';

type VisitorPresenceStatus =
  'ON_SITE' | 'TEMP_OUT' | 'CHECKED_OUT' | 'NO_ACTIVITY';

type MovementLog = {
  _id: string;
  logType: VisitorMovementLogType;
  dateTime: string;
  lookupDate: string;
  description?: string;
  invalidated?: boolean;
};

type VisitorDayActivity = {
  status: VisitorPresenceStatus;
  firstIn: string | null;
  firstInLogId: string | null;
  finalOut: string | null;
  finalOutLogId: string | null;
  events: MovementLog[];
};

const VISITOR_MOVEMENT_TYPES: VisitorMovementLogType[] = [
  'VISITOR_IN',
  'VISITOR_TEMP_OUT',
  'VISITOR_TEMP_IN',
  'VISITOR_OUT',
];

const VisitorLog = () => {
  const dispatch = useDispatch();

  const { timezone, shiftStart, siteName, siteId } = useSelector(
    (state: any) => state.currentSite
  );

  const { firstName, lastName, licensePlate, visitorType, vendorCompany } =
    useSelector((state: any) => state.visitor);

  const visitorLog = useSelector((state: any) => state.visitorLog.items);

  const realtimeVersion = useSelector((state: any) =>
    selectVisitorLogRealtimeVersion(state, siteId)
  );

  const [mutableVisitorLog, setMutableVisitorLog] = useState<any[]>(
    visitorLog ?? []
  );

  const [deletingIds, setDeletingIds] = useState<string[]>([]);
  const [savingIds, setSavingIds] = useState<string[]>([]);
  const [loggingOutIds, setLoggingOutIds] = useState<string[]>([]);
  const [tempOutIds, setTempOutIds] = useState<string[]>([]);
  const [returningIds, setReturningIds] = useState<string[]>([]);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);

  const [tableLoadingBack, setTableLoadingBack] = useState(false);
  const [tableLoadingForward, setTableLoadingForward] = useState(false);
  const [btnDisabled, setBtnDisabled] = useState(true);

  const [editingRowId, setEditingRowId] = useState<string | null>(null);

  const [editedTimes, setEditedTimes] = useState<{
    [visitorId: string]: {
      timeIn?: string;
      timeOut?: string;
      timeInLogId?: string;
      timeOutLogId?: string;
    };
  }>({});

  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const lastInputRef = useRef<HTMLInputElement | null>(null);
  const plateInputRef = useRef<HTMLInputElement | null>(null);
  const companyInputRef = useRef<HTMLInputElement | null>(null);

  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [activeInput, setActiveInput] = useState<
    'first' | 'last' | 'plate' | 'company' | null
  >(null);

  const [fetchDate, setFetchDate] = useState({
    key: moment().format('MMDDYYYY'),
    displayDate: moment().format('MMMM DD, YYYY (dddd)'),
  });

  const [isToday, setIsToday] = useState(true);

  function normalizeTimeHHmm(raw: string): string | null {
    const value = (raw ?? '').trim();

    if (!value) return '';

    const match = value.match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);

    if (!match) return null;

    const hours = Number(match[1]);
    const minutes = Number(match[2]);

    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
      return null;
    }

    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
      2,
      '0'
    )}`;
  }

  /**
   * Resolve an edited HH:mm value around the ACTUAL timestamp of the
   * movement event being edited.
   *
   * This avoids an early-morning ambiguity in the old validation:
   *
   *   HH:mm + fetchDate.key + shiftStart
   *
   * Around an overnight operational-day boundary, that reconstruction can
   * accidentally place a perfectly valid morning time on the following
   * calendar day and incorrectly report it as "future".
   *
   * For an existing log entry we already know the real dateTime. We therefore
   * create the entered clock time on the previous / same / next local calendar
   * day and choose whichever candidate is closest to the original event.
   *
   * Examples:
   *   existing 06:49 -> edit 06:09 = same calendar morning
   *   existing 23:55 -> edit 00:10 = following calendar day
   *   existing 00:10 -> edit 23:55 = previous calendar day
   */
  function resolveEditedMomentFromExistingEvent(
    hhmm: string,
    existingDateTime: string,
    zone: string
  ) {
    const [hour, minute] = hhmm.split(':').map(Number);

    const original = moment(existingDateTime).tz(zone);

    const sameCalendarDay = original
      .clone()
      .hour(hour)
      .minute(minute)
      .second(0)
      .millisecond(0);

    const candidates = [
      sameCalendarDay.clone().subtract(1, 'day'),
      sameCalendarDay,
      sameCalendarDay.clone().add(1, 'day'),
    ];

    return candidates.reduce((closest, candidate) => {
      const closestDistance = Math.abs(closest.diff(original));
      const candidateDistance = Math.abs(candidate.diff(original));

      return candidateDistance < closestDistance ? candidate : closest;
    });
  }

  function getPresenceStatus(
    logType?: VisitorMovementLogType
  ): VisitorPresenceStatus {
    switch (logType) {
      case 'VISITOR_IN':
      case 'VISITOR_TEMP_IN':
        return 'ON_SITE';

      case 'VISITOR_TEMP_OUT':
        return 'TEMP_OUT';

      case 'VISITOR_OUT':
        return 'CHECKED_OUT';

      default:
        return 'NO_ACTIVITY';
    }
  }

  function getStatusLabel(status: VisitorPresenceStatus) {
    switch (status) {
      case 'ON_SITE':
        return 'On Site';

      case 'TEMP_OUT':
        return 'Temporarily Out';

      case 'CHECKED_OUT':
        return 'Checked Out';

      default:
        return 'No Activity';
    }
  }

  function getMovementLabel(logType: VisitorMovementLogType) {
    switch (logType) {
      case 'VISITOR_IN':
        return 'Checked In';

      case 'VISITOR_TEMP_OUT':
        return 'Temporarily Left';

      case 'VISITOR_TEMP_IN':
        return 'Returned';

      case 'VISITOR_OUT':
        return 'Checked Out';
    }
  }

  function getVisitorTypeLabel(type?: string) {
    switch (type) {
      case 'VENDOR':
        return 'Vendor';

      case 'GUEST':
        return 'Guest';

      case 'STAFF':
        return 'Staff';

      default:
        return 'Visitor';
    }
  }

  function getVisitorDayActivity(
    visitor: any,
    lookupKey: string
  ): VisitorDayActivity {
    const events = [...(visitor.logEntries ?? [])]
      .filter(
        (log: any) =>
          log.lookupDate === lookupKey &&
          log.invalidated !== true &&
          VISITOR_MOVEMENT_TYPES.includes(log.logType)
      )
      .sort((a: any, b: any) => {
        const timeDifference =
          new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();

        if (timeDifference !== 0) {
          return timeDifference;
        }

        return String(a._id).localeCompare(String(b._id));
      }) as MovementLog[];

    const latestEvent = events.length ? events[events.length - 1] : undefined;
    const status = getPresenceStatus(latestEvent?.logType);

    const firstArrival = events.find((log) => log.logType === 'VISITOR_IN');

    const finalDeparture =
      status === 'CHECKED_OUT'
        ? [...events].reverse().find((log) => log.logType === 'VISITOR_OUT')
        : undefined;

    return {
      status,

      firstIn: firstArrival
        ? moment.tz(firstArrival.dateTime, timezone).format('HH:mm')
        : null,

      firstInLogId: firstArrival?._id ?? null,

      finalOut: finalDeparture
        ? moment.tz(finalDeparture.dateTime, timezone).format('HH:mm')
        : null,

      finalOutLogId: finalDeparture?._id ?? null,

      events,
    };
  }

  function positionSuggestionDropdown(
    input: HTMLInputElement | null,
    field: 'first' | 'last' | 'plate' | 'company'
  ) {
    if (!input) return;

    const rect = input.getBoundingClientRect();

    setActiveInput(field);

    setDropdownPos({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width,
    });
  }

  useEffect(() => {
    if (!timezone || !shiftStart) return;

    const todayKey = getSiteDateKey(new Date(), shiftStart, timezone);
    const todayMoment = moment.tz(todayKey, 'MMDDYYYY', timezone);

    setFetchDate({
      key: todayKey,
      displayDate: todayMoment.format('MMMM DD, YYYY (dddd)'),
    });

    setIsToday(true);

    (async () => {
      try {
        if (!siteId) return;

        const res = await getSiteVisitorsByDay({
          siteId,
          dateAccessKey: todayKey,
        });

        if (res?.success) {
          setMutableVisitorLog(res.data ?? []);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [timezone, shiftStart, siteId]);

  async function reloadLogs() {
    const reload = await updateLogEntriesAndVisitors();

    if (reload.success) {
      dispatch(setVisitorLog(reload.data.visitors));
      dispatch(setLogEntires(reload.data.logEntries));

      setMutableVisitorLog(reload.data.visitors);
    }
  }

  useEffect(() => {
    if (isToday) {
      setMutableVisitorLog(visitorLog ?? []);
    }
  }, [visitorLog, isToday]);

  useEffect(() => {
    if (!siteId || !isToday || realtimeVersion === 0) {
      return;
    }

    let cancelled = false;

    async function refetchRealtimeVisitorLog() {
      try {
        const todayKey = getSiteDateKey(new Date(), shiftStart, timezone);

        const res = await getSiteVisitorsByDay({
          siteId,
          dateAccessKey: todayKey,
        });

        if (!cancelled && res?.success) {
          setMutableVisitorLog(res.data ?? []);
          dispatch(setVisitorLog(res.data ?? []));
        }
      } catch (error) {
        console.error('Realtime visitor log refresh failed:', error);
      }
    }

    void refetchRealtimeVisitorLog();

    return () => {
      cancelled = true;
    };
  }, [realtimeVersion, siteId, isToday, shiftStart, timezone, dispatch]);

  useEffect(() => {
    const input =
      activeInput === 'first'
        ? firstName
        : activeInput === 'last'
          ? lastName
          : activeInput === 'plate'
            ? licensePlate
            : activeInput === 'company'
              ? vendorCompany
              : '';

    const delay = setTimeout(async () => {
      if (input.length > 1 && siteId && activeInput) {
        setSearching(true);

        try {
          const res = await searchVisitors({
            query: input,
            field: activeInput,
            siteId,
          });

          if (res?.success && Array.isArray(res.data)) {
            setSuggestions(res.data);
          } else {
            setSuggestions([]);
          }
        } catch (error) {
          console.error('Visitor search failed:', error);
          setSuggestions([]);
        } finally {
          setSearching(false);
        }
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(delay);
  }, [firstName, lastName, vendorCompany, licensePlate, activeInput, siteId]);

  useEffect(() => {
    if (visitorType !== 'VENDOR') {
      dispatch(setVendorCompany(''));

      if (activeInput === 'company') {
        setSuggestions([]);
        setActiveInput(null);
      }
    }
  }, [visitorType, dispatch, activeInput]);

  useEffect(() => {
    setBtnDisabled(() => {
      const isVendor = visitorType === 'VENDOR';

      const hasBasicFields =
        firstName.length > 1 &&
        lastName.length > 1 &&
        licensePlate.length > 1 &&
        visitorType.length > 1;

      const hasCompany =
        !isVendor || (isVendor && (vendorCompany ?? '').length > 1);

      return !(hasBasicFields && hasCompany);
    });
  }, [firstName, lastName, licensePlate, visitorType, vendorCompany]);

  async function handleDateBack() {
    setTableLoadingBack(true);

    try {
      const current = moment.tz(fetchDate.key, 'MMDDYYYY', timezone);
      const newDate = current.clone().subtract(1, 'day');

      const res = await getSiteVisitorsByDay({
        siteId,
        dateAccessKey: newDate.format('MMDDYYYY'),
      });

      if (res.success) {
        const todayKey = getSiteDateKey(new Date(), shiftStart, timezone);

        setFetchDate({
          key: newDate.format('MMDDYYYY'),
          displayDate: newDate.format('MMMM DD, YYYY (dddd)'),
        });

        setIsToday(newDate.format('MMDDYYYY') === todayKey);
        setMutableVisitorLog(res.data ?? []);
        setExpandedRowId(null);
      }
    } catch (error) {
      alert('Something went wrong. Please refresh the page and try again.');
    } finally {
      setTableLoadingBack(false);
    }
  }

  async function handleDateForward() {
    setTableLoadingForward(true);

    try {
      const current = moment.tz(fetchDate.key, 'MMDDYYYY', timezone);
      const newDate = current.clone().add(1, 'day');

      const todayKey = getSiteDateKey(new Date(), shiftStart, timezone);
      const todayMoment = moment.tz(todayKey, 'MMDDYYYY', timezone);

      if (newDate.isAfter(todayMoment, 'day')) {
        return;
      }

      const res = await getSiteVisitorsByDay({
        siteId,
        dateAccessKey: newDate.format('MMDDYYYY'),
      });

      if (res.success) {
        setFetchDate({
          key: newDate.format('MMDDYYYY'),
          displayDate: newDate.format('MMMM DD, YYYY (dddd)'),
        });

        setIsToday(newDate.isSame(todayMoment, 'day'));
        setMutableVisitorLog(res.data ?? []);
        setExpandedRowId(null);
      }
    } catch (error) {
      alert('Something went wrong. Please refresh the page and try again.');
    } finally {
      setTableLoadingForward(false);
    }
  }

  async function handleSubmit() {
    setBtnDisabled(true);

    const payload = {
      siteName,
      siteId,
      firstName,
      lastName,
      licensePlate,
      visitorType,
      vendorCompany,
      timeIn: new Date(),
      timeOut: null,
      timezone,
    };

    try {
      const res = await addVisitor(payload);

      if (res.success) {
        await reloadLogs();
        dispatch(clearState());

        setSuggestions([]);
        setActiveInput(null);
        setDropdownPos(null);
      } else {
        alert(res.msg ?? 'Unable to check visitor in.');
        setBtnDisabled(false);
      }
    } catch (error) {
      console.error(error);
      alert('Unable to check visitor in.');
      setBtnDisabled(false);
    }
  }

  async function handleTemporaryLeave(visitorId: string) {
    if (tempOutIds.includes(visitorId)) return;

    setTempOutIds((prev) => [...prev, visitorId]);

    try {
      const res = await temporaryLeaveVisitor(visitorId);

      if (res.success) {
        await reloadLogs();
      } else {
        alert(res.msg ?? 'Unable to mark visitor temporarily out.');
      }
    } catch (error) {
      console.error(error);
      alert('Unable to mark visitor temporarily out.');
    } finally {
      setTempOutIds((prev) => prev.filter((id) => id !== visitorId));
    }
  }

  async function handleReturn(visitorId: string) {
    if (returningIds.includes(visitorId)) return;

    setReturningIds((prev) => [...prev, visitorId]);

    try {
      const res = await returnVisitor(visitorId);

      if (res.success) {
        await reloadLogs();
      } else {
        alert(res.msg ?? 'Unable to mark visitor returned.');
      }
    } catch (error) {
      console.error(error);
      alert('Unable to mark visitor returned.');
    } finally {
      setReturningIds((prev) => prev.filter((id) => id !== visitorId));
    }
  }

  async function handleLogout(visitorId: string) {
    if (loggingOutIds.includes(visitorId)) return;

    setLoggingOutIds((prev) => [...prev, visitorId]);

    try {
      const res = await logoutVisitor(visitorId);

      if (res.success) {
        await reloadLogs();
        dispatch(clearState());
      } else {
        alert(res.msg ?? 'Unable to check visitor out.');
      }
    } catch (error) {
      console.error(error);
      alert('Unable to check visitor out.');
    } finally {
      setLoggingOutIds((prev) => prev.filter((id) => id !== visitorId));
    }
  }

  async function handleUpdateTimes(entry: any) {
    if (savingIds.includes(entry._id)) return;

    const changes = editedTimes[entry._id];

    if (!changes) return;

    const payload: { logId: string; newTime: string }[] = [];

    if (changes.timeIn && changes.timeInLogId) {
      payload.push({
        logId: changes.timeInLogId,
        newTime: changes.timeIn,
      });
    }

    if (changes.timeOut && changes.timeOutLogId) {
      payload.push({
        logId: changes.timeOutLogId,
        newTime: changes.timeOut,
      });
    }

    if (payload.length === 0) {
      setEditingRowId(null);
      return;
    }

    setSavingIds((prev) => [...prev, entry._id]);

    try {
      await updateLogEntryTimes(payload, timezone);
      await reloadLogs();

      setEditingRowId(null);

      setEditedTimes((prev) => {
        const updated = { ...prev };
        delete updated[entry._id];
        return updated;
      });
    } catch (error) {
      console.error(error);
      alert('Could not save the time change. Please try again.');
    } finally {
      setSavingIds((prev) => prev.filter((id) => id !== entry._id));
    }
  }

  async function handleDeleteVisitorEntry(visitorId: string) {
    setDeletingIds((prev) => [...prev, visitorId]);

    try {
      const resp = await invalidateEntry({
        dateKey: fetchDate.key,
        referenceId: visitorId,
      });

      if (resp?.success) {
        await reloadLogs();
      }
    } catch (error) {
      console.error(error);
      alert('Unable to delete visitor entry.');
    } finally {
      setDeletingIds((prev) => prev.filter((id) => id !== visitorId));
    }
  }

  function handleEditableTimeBlur(
    event: React.FocusEvent<HTMLTableCellElement>,
    entry: any,
    type: 'timeIn' | 'timeOut',
    activity: VisitorDayActivity
  ) {
    const raw = event.currentTarget.textContent?.trim() || '';

    const prevValue = type === 'timeIn' ? activity.firstIn : activity.finalOut;

    const normalized = normalizeTimeHHmm(raw);

    if (normalized === null) {
      event.currentTarget.textContent = prevValue ?? '';
      setEditingRowId(null);
      return;
    }

    event.currentTarget.textContent = normalized;

    if (normalized === '') {
      event.currentTarget.textContent = prevValue ?? '';
      setEditingRowId(null);
      return;
    }

    /*
     * Resolve the edited time from the actual movement event instead of from
     * the viewed operational-date key.
     *
     * The event timestamp removes the calendar-day ambiguity that occurs in
     * the early morning around a shift boundary.
     */
    const editedLogId =
      type === 'timeIn' ? activity.firstInLogId : activity.finalOutLogId;

    const editedEvent = activity.events.find(
      (movement) => movement._id === editedLogId
    );

    /*
     * editedEvent should always exist because the cell is editable only when
     * its movement log ID exists. Keep the old operational resolver as a safe
     * fallback for legacy/incomplete records.
     */
    const enteredAbs = editedEvent
      ? resolveEditedMomentFromExistingEvent(
          normalized,
          editedEvent.dateTime,
          timezone
        )
      : resolveOperationalMomentFromHHmm(
          normalized,
          fetchDate.key,
          shiftStart,
          timezone
        );

    const now = moment().tz(timezone);

    if (enteredAbs.isAfter(now)) {
      alert('Time cannot be in the future.');
      event.currentTarget.textContent = prevValue ?? '';
      setEditingRowId(null);
      return;
    }

    if (type === 'timeIn' && activity.finalOut) {
      const finalOutEvent = activity.events.find(
        (movement) => movement._id === activity.finalOutLogId
      );

      /*
       * Use the real absolute checkout timestamp whenever available. That is
       * more reliable than rebuilding a date from HH:mm + operational date.
       */
      const finalOutAbs = finalOutEvent
        ? moment(finalOutEvent.dateTime).tz(timezone)
        : resolveOperationalMomentFromHHmm(
            activity.finalOut,
            fetchDate.key,
            shiftStart,
            timezone
          );

      if (enteredAbs.isAfter(finalOutAbs)) {
        alert('First check-in cannot be after final checkout.');
        event.currentTarget.textContent = activity.firstIn ?? '';
        setEditingRowId(null);
        return;
      }
    }

    if (type === 'timeOut' && activity.firstIn) {
      const firstInEvent = activity.events.find(
        (movement) => movement._id === activity.firstInLogId
      );

      /*
       * Same rule for checkout validation: compare against the actual stored
       * check-in timestamp rather than reconstructing its date.
       */
      const firstInAbs = firstInEvent
        ? moment(firstInEvent.dateTime).tz(timezone)
        : resolveOperationalMomentFromHHmm(
            activity.firstIn,
            fetchDate.key,
            shiftStart,
            timezone
          );

      if (enteredAbs.isBefore(firstInAbs)) {
        alert('Final checkout cannot be before first check-in.');
        event.currentTarget.textContent = activity.finalOut ?? '';
        setEditingRowId(null);
        return;
      }
    }

    setEditedTimes((prev) => ({
      ...prev,

      [entry._id]: {
        ...prev[entry._id],

        [type]: normalized,

        [`${type}LogId`]:
          type === 'timeIn'
            ? (activity.firstInLogId ?? undefined)
            : (activity.finalOutLogId ?? undefined),
      },
    }));
  }

  const visitorCount = mutableVisitorLog?.length ?? 0;

  return (
    <div className={styles.wrapper}>
      <VisitorProfile />

      <div className={styles.leftContainer}>
        <div className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>Visitor Management</p>
            <h1 className={styles.pageTitle}>Visitor Log</h1>
            <p className={styles.pageSubtitle}>
              Check visitors in, monitor presence and NDA status, and review
              their complete daily access trail.
            </p>
          </div>

          <div className={styles.siteBadge}>
            <span className={styles.siteDot} />
            {siteName || 'Current Site'}
          </div>
        </div>

        <div className={styles.dateSelector}>
          <button
            type="button"
            className={styles.dateArrowButton}
            onClick={handleDateBack}
            aria-label="Previous day"
          >
            <FaCircleArrowLeft
              className={`${styles.dateArrow} ${
                tableLoadingBack ? styles.flashArrow : ''
              }`}
            />
          </button>

          <div className={styles.dateCopy}>
            <span className={styles.dateCaption}>Viewing</span>
            <strong>{fetchDate.displayDate}</strong>
          </div>

          <button
            type="button"
            className={`${styles.dateArrowButton} ${
              isToday ? styles.disabledArrowButton : ''
            }`}
            onClick={!isToday ? handleDateForward : undefined}
            disabled={isToday}
            aria-label="Next day"
          >
            <FaCircleArrowRight
              className={`${styles.dateArrow} ${
                tableLoadingForward ? styles.flashArrow : ''
              }`}
            />
          </button>

          {isToday && <span className={styles.todayBadge}>Today</span>}
        </div>

        <section className={styles.card}>
          <div className={styles.cardHeader}>
            <div>
              <h2 className={styles.cardTitle}>Add Visitor</h2>
              <p className={styles.cardSubtitle}>
                Create a new visitor check-in for the current operational day.
              </p>
            </div>
          </div>

          <div className={styles.entryHeader}>
            <div className={styles.inputContainer}>
              <label className={styles.label}>Group</label>

              <select
                value={visitorType}
                onChange={(e) => dispatch(setType(e.target.value))}
                className={styles.inputDropDown}
              >
                <option value="">Select</option>
                <option value="VENDOR">Vendor</option>
                <option value="GUEST">Guest</option>
                <option value="STAFF">Staff</option>
              </select>
            </div>

            <div className={styles.inputContainer}>
              <label className={styles.label}>Company</label>

              <div className={styles.inputRelative}>
                <input
                  ref={companyInputRef}
                  value={vendorCompany}
                  onFocus={() => {
                    if (visitorType === 'VENDOR') {
                      positionSuggestionDropdown(
                        companyInputRef.current,
                        'company'
                      );
                    }
                  }}
                  onChange={(e) => {
                    if (visitorType === 'VENDOR') {
                      dispatch(setVendorCompany(e.target.value));
                    }
                  }}
                  placeholder={
                    visitorType === 'VENDOR' ? 'Company name' : 'Vendor only'
                  }
                  className={`${styles.inputField} ${
                    visitorType !== 'VENDOR' ? styles.disabledInput : ''
                  }`}
                  type="text"
                  disabled={visitorType !== 'VENDOR'}
                />

                {activeInput === 'company' && searching && (
                  <div className={styles.scaleLoaderContainer}>
                    <ScaleLoader height={10} width={2} color="#71717a" />
                  </div>
                )}
              </div>
            </div>

            {[
              {
                label: 'First Name',
                inputType: 'first' as const,
                value: firstName,
                ref: firstInputRef,
              },
              {
                label: 'Last Name',
                inputType: 'last' as const,
                value: lastName,
                ref: lastInputRef,
              },
              {
                label: 'License Plate',
                inputType: 'plate' as const,
                value: licensePlate,
                ref: plateInputRef,
              },
            ].map((field) => (
              <div key={field.inputType} className={styles.inputContainer}>
                <label className={styles.label}>{field.label}</label>

                <div className={styles.inputRelative}>
                  <input
                    ref={field.ref}
                    value={field.value}
                    onFocus={() =>
                      positionSuggestionDropdown(
                        field.ref.current,
                        field.inputType
                      )
                    }
                    onChange={(e) =>
                      dispatch(
                        field.inputType === 'first'
                          ? setFirst(e.target.value)
                          : field.inputType === 'last'
                            ? setLast(e.target.value)
                            : setPlate(e.target.value.toUpperCase())
                      )
                    }
                    className={`${styles.inputField} ${
                      field.inputType === 'plate'
                        ? styles.inputFieldUppercase
                        : ''
                    }`}
                    type="text"
                  />

                  {activeInput === field.inputType && searching && (
                    <div className={styles.scaleLoaderContainer}>
                      <ScaleLoader height={10} width={2} color="#71717a" />
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={handleSubmit}
              disabled={btnDisabled}
              className={`${styles.submitBtn} ${
                btnDisabled ? styles.submitBtnDisabled : ''
              }`}
            >
              Check In
            </button>
          </div>
        </section>

        {suggestions.length > 0 && activeInput && dropdownPos && (
          <ul
            className={styles.suggestionDropdown}
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 10000,
            }}
          >
            {suggestions
              .filter(
                (suggestion) =>
                  !mutableVisitorLog?.some(
                    (visitor: any) =>
                      visitor.firstName === suggestion.firstName &&
                      visitor.lastName === suggestion.lastName &&
                      visitor.licensePlate === suggestion.licensePlate
                  )
              )
              .map((suggestion, index) => (
                <li
                  key={suggestion._id ?? index}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    dispatch(setFirst(suggestion.firstName ?? ''));
                    dispatch(setLast(suggestion.lastName ?? ''));
                    dispatch(setPlate(suggestion.licensePlate ?? ''));
                    dispatch(setType(suggestion.visitorType ?? ''));
                    dispatch(
                      setVendorCompany(suggestion.vendorCompanyName ?? '')
                    );

                    setSuggestions([]);
                    setActiveInput(null);
                    setDropdownPos(null);
                  }}
                >
                  <strong>
                    {suggestion.firstName} {suggestion.lastName}
                  </strong>

                  <span>
                    {suggestion.licensePlate || 'No plate'}
                    {suggestion.vendorCompanyName
                      ? ` • ${suggestion.vendorCompanyName}`
                      : ''}
                  </span>
                </li>
              ))}
          </ul>
        )}

        <section className={`${styles.card} ${styles.tableCard}`}>
          <div className={styles.tableCardHeader}>
            <div>
              <h2 className={styles.cardTitle}>Visitors</h2>
              <p className={styles.cardSubtitle}>
                {visitorCount === 1
                  ? '1 visitor recorded for this operational day.'
                  : `${visitorCount} visitors recorded for this operational day.`}
              </p>
            </div>

            <div className={styles.legend}>
              <span className={`${styles.statusBadge} ${styles.statusOnSite}`}>
                On Site
              </span>

              <span className={`${styles.statusBadge} ${styles.statusTempOut}`}>
                Temporarily Out
              </span>

              <span className={`${styles.ndaBadge} ${styles.ndaCurrent}`}>
                NDA Current
              </span>

              <span className={`${styles.ndaBadge} ${styles.ndaRequired}`}>
                NDA Required
              </span>
            </div>
          </div>

          {visitorCount === 0 ? (
            <div className={styles.emptyState}>
              <h3>No visitors recorded</h3>
              <p>
                Visitors checked in for this operational day will appear here.
              </p>
            </div>
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.tableHead}>Visitor</th>
                    <th className={styles.tableHead}>Group</th>
                    <th className={styles.tableHead}>Plate</th>
                    <th className={styles.tableHead}>Company</th>
                    <th className={styles.tableHead}>First In</th>
                    <th className={styles.tableHead}>Final Out</th>
                    <th className={styles.tableHead}>Status</th>
                    <th className={styles.tableHead}>NDA</th>
                    <th className={`${styles.tableHead} ${styles.actionHead}`}>
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {mutableVisitorLog.map((entry: any) => {
                    const activity = getVisitorDayActivity(
                      entry,
                      fetchDate.key
                    );

                    const expanded = expandedRowId === entry._id;

                    const movementBusy =
                      tempOutIds.includes(entry._id) ||
                      returningIds.includes(entry._id) ||
                      loggingOutIds.includes(entry._id);

                    return (
                      <Fragment key={entry._id}>
                        <tr
                          className={`${styles.tableRow} ${
                            expanded ? styles.tableRowExpanded : ''
                          }`}
                          onClick={() =>
                            setExpandedRowId(expanded ? null : entry._id)
                          }
                        >
                          <td className={styles.tableBody}>
                            <div className={styles.visitorCell}>
                              <div className={styles.avatar}>
                                {(entry.firstName?.[0] ?? '').toUpperCase()}
                                {(entry.lastName?.[0] ?? '').toUpperCase()}
                              </div>

                              <div>
                                <strong className={styles.visitorName}>
                                  {entry.firstName} {entry.lastName}
                                </strong>

                                <span className={styles.activityCount}>
                                  {activity.events.length}{' '}
                                  {activity.events.length === 1
                                    ? 'movement'
                                    : 'movements'}
                                </span>
                              </div>
                            </div>
                          </td>

                          <td className={styles.tableBody}>
                            <span className={styles.groupBadge}>
                              {getVisitorTypeLabel(entry.visitorType)}
                            </span>
                          </td>

                          <td className={styles.tableBody}>
                            <span className={styles.plate}>
                              {entry.licensePlate || '—'}
                            </span>
                          </td>

                          <td className={styles.tableBody}>
                            {entry.visitorType === 'VENDOR'
                              ? entry.vendorCompanyName || '—'
                              : '—'}
                          </td>

                          <td
                            className={`${styles.tableBody} ${
                              isToday && activity.firstInLogId
                                ? styles.editableCell
                                : ''
                            }`}
                            contentEditable={isToday && !!activity.firstInLogId}
                            suppressContentEditableWarning
                            onClick={(event) => event.stopPropagation()}
                            onFocus={
                              isToday && activity.firstInLogId
                                ? () => setEditingRowId(entry._id)
                                : undefined
                            }
                            onBlur={
                              isToday && activity.firstInLogId
                                ? (event) =>
                                    handleEditableTimeBlur(
                                      event,
                                      entry,
                                      'timeIn',
                                      activity
                                    )
                                : undefined
                            }
                          >
                            {activity.firstIn || '—'}
                          </td>

                          <td
                            className={`${styles.tableBody} ${
                              isToday && activity.finalOutLogId
                                ? styles.editableCell
                                : ''
                            }`}
                            contentEditable={
                              isToday && !!activity.finalOutLogId
                            }
                            suppressContentEditableWarning
                            onClick={(event) => event.stopPropagation()}
                            onFocus={
                              isToday && activity.finalOutLogId
                                ? () => setEditingRowId(entry._id)
                                : undefined
                            }
                            onBlur={
                              isToday && activity.finalOutLogId
                                ? (event) =>
                                    handleEditableTimeBlur(
                                      event,
                                      entry,
                                      'timeOut',
                                      activity
                                    )
                                : undefined
                            }
                          >
                            {activity.finalOut || '—'}
                          </td>

                          <td className={styles.tableBody}>
                            <span
                              className={`${styles.statusBadge} ${
                                activity.status === 'ON_SITE'
                                  ? styles.statusOnSite
                                  : activity.status === 'TEMP_OUT'
                                    ? styles.statusTempOut
                                    : styles.statusCheckedOut
                              }`}
                            >
                              {getStatusLabel(activity.status)}
                            </span>
                          </td>

                          <td className={styles.tableBody}>
                            <span
                              className={`${styles.ndaBadge} ${
                                entry.ndaCurrent
                                  ? styles.ndaCurrent
                                  : styles.ndaRequired
                              }`}
                            >
                              {entry.ndaCurrent
                                ? 'NDA Current'
                                : 'NDA Required'}
                            </span>
                          </td>

                          <td
                            className={`${styles.tableBody} ${styles.actionCell}`}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {editingRowId === entry._id ? (
                              savingIds.includes(entry._id) ? (
                                <div className={styles.inlineLoader}>
                                  <ClockLoader size={20} />
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  className={`${styles.iconButton} ${styles.saveButton}`}
                                  title="Save time changes"
                                  onClick={() => handleUpdateTimes(entry)}
                                >
                                  <FaRegCheckCircle />
                                </button>
                              )
                            ) : (
                              <div className={styles.actionGroup}>
                                {isToday && activity.status === 'ON_SITE' && (
                                  <>
                                    <button
                                      type="button"
                                      className={`${styles.actionButton} ${styles.tempButton}`}
                                      disabled={movementBusy}
                                      onClick={() =>
                                        handleTemporaryLeave(entry._id)
                                      }
                                    >
                                      {tempOutIds.includes(entry._id) ? (
                                        <ClockLoader size={15} />
                                      ) : (
                                        <>
                                          <MdOutlineDirectionsWalk />
                                          Temp Out
                                        </>
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      className={`${styles.actionButton} ${styles.checkoutButton}`}
                                      disabled={movementBusy}
                                      onClick={() => handleLogout(entry._id)}
                                    >
                                      {loggingOutIds.includes(entry._id) ? (
                                        <ClockLoader size={15} />
                                      ) : (
                                        <>
                                          <IoLogOut />
                                          Check Out
                                        </>
                                      )}
                                    </button>
                                  </>
                                )}

                                {isToday && activity.status === 'TEMP_OUT' && (
                                  <>
                                    <button
                                      type="button"
                                      className={`${styles.actionButton} ${styles.returnButton}`}
                                      disabled={movementBusy}
                                      onClick={() => handleReturn(entry._id)}
                                    >
                                      {returningIds.includes(entry._id) ? (
                                        <ClockLoader size={15} />
                                      ) : (
                                        <>
                                          <IoReturnUpBack />
                                          Return
                                        </>
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      className={`${styles.actionButton} ${styles.checkoutButton}`}
                                      disabled={movementBusy}
                                      onClick={() => handleLogout(entry._id)}
                                    >
                                      {loggingOutIds.includes(entry._id) ? (
                                        <ClockLoader size={15} />
                                      ) : (
                                        <>
                                          <IoLogOut />
                                          Final Out
                                        </>
                                      )}
                                    </button>
                                  </>
                                )}

                                {isToday && (
                                  <button
                                    type="button"
                                    className={`${styles.iconButton} ${styles.deleteButton}`}
                                    title="Delete visitor log entry"
                                    disabled={deletingIds.includes(entry._id)}
                                    onClick={() =>
                                      handleDeleteVisitorEntry(entry._id)
                                    }
                                  >
                                    {deletingIds.includes(entry._id) ? (
                                      <ClockLoader size={15} />
                                    ) : (
                                      <MdDeleteForever />
                                    )}
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className={styles.expandButton}
                                  onClick={() =>
                                    setExpandedRowId(
                                      expanded ? null : entry._id
                                    )
                                  }
                                  aria-label={
                                    expanded
                                      ? 'Collapse visitor activity'
                                      : 'Expand visitor activity'
                                  }
                                >
                                  {expanded ? (
                                    <IoChevronUp />
                                  ) : (
                                    <IoChevronDown />
                                  )}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>

                        {expanded && (
                          <tr className={styles.expandedRow}>
                            <td colSpan={9} className={styles.expandedCell}>
                              <div className={styles.expandedContent}>
                                <div className={styles.detailGrid}>
                                  <div className={styles.detailItem}>
                                    <span>Visitor Type</span>
                                    <strong>
                                      {getVisitorTypeLabel(entry.visitorType)}
                                    </strong>
                                  </div>

                                  <div className={styles.detailItem}>
                                    <span>Company</span>
                                    <strong>
                                      {entry.vendorCompanyName || '—'}
                                    </strong>
                                  </div>

                                  <div className={styles.detailItem}>
                                    <span>License Plate</span>
                                    <strong>{entry.licensePlate || '—'}</strong>
                                  </div>

                                  <div className={styles.detailItem}>
                                    <span>Current Status</span>
                                    <strong>
                                      {getStatusLabel(activity.status)}
                                    </strong>
                                  </div>

                                  <div className={styles.detailItem}>
                                    <span>NDA</span>
                                    <strong>
                                      {entry.ndaCurrent
                                        ? 'Current'
                                        : 'Required'}
                                    </strong>
                                  </div>
                                </div>

                                <div className={styles.timelineSection}>
                                  <div className={styles.timelineHeader}>
                                    <div>
                                      <h3>Daily Activity</h3>
                                      <p>
                                        Complete visitor movement trail for this
                                        operational day.
                                      </p>
                                    </div>
                                  </div>

                                  {activity.events.length === 0 ? (
                                    <p className={styles.noActivity}>
                                      No movement events recorded.
                                    </p>
                                  ) : (
                                    <div className={styles.timeline}>
                                      {activity.events.map((event, index) => (
                                        <div
                                          key={event._id}
                                          className={styles.timelineItem}
                                        >
                                          <div className={styles.timelineRail}>
                                            <span
                                              className={`${styles.timelineDot} ${
                                                event.logType ===
                                                'VISITOR_TEMP_OUT'
                                                  ? styles.timelineDotTemp
                                                  : event.logType ===
                                                      'VISITOR_OUT'
                                                    ? styles.timelineDotOut
                                                    : styles.timelineDotIn
                                              }`}
                                            />

                                            {index <
                                              activity.events.length - 1 && (
                                              <span
                                                className={
                                                  styles.timelineConnector
                                                }
                                              />
                                            )}
                                          </div>

                                          <div className={styles.timelineTime}>
                                            {moment
                                              .tz(event.dateTime, timezone)
                                              .format('hh:mm A')}
                                          </div>

                                          <div className={styles.timelineCopy}>
                                            <strong>
                                              {getMovementLabel(event.logType)}
                                            </strong>

                                            {event.description && (
                                              <span>{event.description}</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default VisitorLog;
